// src/components/diary/WorkspaceSelector.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Building2, Users, User, ChevronDown, Check, Plus, Settings,
  Briefcase, GraduationCap, Church, Heart, Landmark, Building,
  Search, Crown
} from 'lucide-react';
import { workspaceService, WORKSPACE_TYPES, ORGANISATION_TYPES } from '../../services/workspaceService';

// Icon mapping
const ORG_ICONS = {
  business: Building2,
  school: GraduationCap,
  church: Church,
  npo: Heart,
  government: Landmark,
  cooperative: Users,
  agency: Briefcase,
  other: Building
};

export const WorkspaceSelector = ({ 
  bookId, 
  user,
  currentWorkspaceId,
  onWorkspaceChange,
  onOpenSettings,
  compact = false
}) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (bookId) loadWorkspaces();
  }, [bookId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadWorkspaces = async () => {
    setLoading(false);
    try {
      const data = await workspaceService.getWorkspaces(bookId);
      setWorkspaces(data);
      
      // Auto-select personal workspace if none selected
      if (!currentWorkspaceId && data.length > 0) {
        const personal = data.find(w => w.type === WORKSPACE_TYPES.PERSONAL);
        if (personal) {
          onWorkspaceChange?.(personal);
        }
      }
    } catch (error) {
      console.error('Error loading workspaces:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentWorkspace = workspaces.find(w => w.id === currentWorkspaceId) || 
    workspaces.find(w => w.type === WORKSPACE_TYPES.PERSONAL);

  const getWorkspaceIcon = (workspace) => {
    if (workspace.type === WORKSPACE_TYPES.PERSONAL) return User;
    if (workspace.type === WORKSPACE_TYPES.ORGANISATION) {
      return ORG_ICONS[workspace.organisationType] || Building2;
    }
    return Users;
  };

  const getWorkspaceColor = (workspace) => {
    if (workspace.type === WORKSPACE_TYPES.PERSONAL) return 'blue';
    if (workspace.type === WORKSPACE_TYPES.ORGANISATION) return 'purple';
    return 'green';
  };

  const filteredWorkspaces = workspaces.filter(w => 
    w.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedWorkspaces = {
    personal: filteredWorkspaces.filter(w => w.type === WORKSPACE_TYPES.PERSONAL),
    organisations: filteredWorkspaces.filter(w => w.type === WORKSPACE_TYPES.ORGANISATION),
    teams: filteredWorkspaces.filter(w => w.type === WORKSPACE_TYPES.TEAM)
  };

  if (loading || !currentWorkspace) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 ${compact ? '' : 'bg-white rounded-lg border border-gray-200'}`}>
        <div className="w-8 h-8 rounded-lg bg-gray-200 animate-pulse" />
        <div className={compact ? 'hidden' : 'w-24 h-4 bg-gray-200 rounded animate-pulse'} />
      </div>
    );
  }

  const WorkspaceIcon = getWorkspaceIcon(currentWorkspace);
  const colorClass = getWorkspaceColor(currentWorkspace);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 transition-all ${
          compact 
            ? 'p-2 hover:bg-gray-100 rounded-lg'
            : 'px-3 py-2 bg-white rounded-lg border border-gray-200 hover:border-gray-300 shadow-sm min-w-[200px]'
        }`}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          colorClass === 'blue' ? 'bg-blue-100' :
          colorClass === 'purple' ? 'bg-purple-100' : 'bg-green-100'
        }`}>
          <WorkspaceIcon className={`w-4 h-4 ${
            colorClass === 'blue' ? 'text-blue-600' :
            colorClass === 'purple' ? 'text-purple-600' : 'text-green-600'
          }`} />
        </div>

        {!compact && (
          <>
            <div className="flex-1 text-left min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{currentWorkspace.name}</div>
              <div className="text-xs text-gray-500 truncate">
                {currentWorkspace.type === WORKSPACE_TYPES.PERSONAL ? 'Personal' :
                 currentWorkspace.type === WORKSPACE_TYPES.ORGANISATION 
                   ? ORGANISATION_TYPES[currentWorkspace.organisationType]?.label || 'Organisation'
                   : 'Team'}
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50">
          {/* Search */}
          {workspaces.length > 3 && (
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search workspaces..."
                  className="w-full pl-9 pr-3 py-2 bg-gray-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            </div>
          )}

          <div className="max-h-80 overflow-y-auto">
            {/* Personal */}
            {groupedWorkspaces.personal.length > 0 && (
              <div className="p-2">
                <div className="text-xs font-medium text-gray-500 uppercase px-2 py-1">Personal</div>
                {groupedWorkspaces.personal.map(workspace => (
                  <WorkspaceOption
                    key={workspace.id}
                    workspace={workspace}
                    isSelected={workspace.id === currentWorkspaceId}
                    onClick={() => {
                      onWorkspaceChange?.(workspace);
                      setIsOpen(false);
                    }}
                    user={user}
                  />
                ))}
              </div>
            )}

            {/* Organisations */}
            {groupedWorkspaces.organisations.length > 0 && (
              <div className="p-2 border-t border-gray-100">
                <div className="text-xs font-medium text-gray-500 uppercase px-2 py-1">Organisations</div>
                {groupedWorkspaces.organisations.map(workspace => (
                  <WorkspaceOption
                    key={workspace.id}
                    workspace={workspace}
                    isSelected={workspace.id === currentWorkspaceId}
                    onClick={() => {
                      onWorkspaceChange?.(workspace);
                      setIsOpen(false);
                    }}
                    user={user}
                  />
                ))}
              </div>
            )}

            {/* Teams */}
            {groupedWorkspaces.teams.length > 0 && (
              <div className="p-2 border-t border-gray-100">
                <div className="text-xs font-medium text-gray-500 uppercase px-2 py-1">Teams / Groups</div>
                {groupedWorkspaces.teams.map(workspace => (
                  <WorkspaceOption
                    key={workspace.id}
                    workspace={workspace}
                    isSelected={workspace.id === currentWorkspaceId}
                    onClick={() => {
                      onWorkspaceChange?.(workspace);
                      setIsOpen(false);
                    }}
                    user={user}
                  />
                ))}
              </div>
            )}

            {/* Empty State */}
            {filteredWorkspaces.length === 0 && searchQuery && (
              <div className="p-4 text-center text-sm text-gray-500">
                No workspaces match "{searchQuery}"
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="p-2 border-t border-gray-100 bg-gray-50">
            <button
              onClick={() => {
                onOpenSettings?.();
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Settings className="w-4 h-4" />
              Manage Workspaces
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Workspace Option Component
const WorkspaceOption = ({ workspace, isSelected, onClick, user }) => {
  const isOwner = workspace.ownerId === user?.uid;
  
  const getIcon = () => {
    if (workspace.type === WORKSPACE_TYPES.PERSONAL) return User;
    if (workspace.type === WORKSPACE_TYPES.ORGANISATION) {
      return ORG_ICONS[workspace.organisationType] || Building2;
    }
    return Users;
  };

  const getColorClass = () => {
    if (workspace.type === WORKSPACE_TYPES.PERSONAL) return 'blue';
    if (workspace.type === WORKSPACE_TYPES.ORGANISATION) return 'purple';
    return 'green';
  };

  const Icon = getIcon();
  const colorClass = getColorClass();

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg transition-colors ${
        isSelected ? 'bg-gray-100' : 'hover:bg-gray-50'
      }`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
        colorClass === 'blue' ? 'bg-blue-100' :
        colorClass === 'purple' ? 'bg-purple-100' : 'bg-green-100'
      }`}>
        <Icon className={`w-4 h-4 ${
          colorClass === 'blue' ? 'text-blue-600' :
          colorClass === 'purple' ? 'text-purple-600' : 'text-green-600'
        }`} />
      </div>

      <div className="flex-1 text-left min-w-0">
        <div className="flex items-center gap-1">
          <span className="text-sm font-medium text-gray-900 truncate">{workspace.name}</span>
          {isOwner && <Crown className="w-3 h-3 text-amber-500 shrink-0" />}
        </div>
        {workspace.memberIds?.length > 1 && (
          <div className="text-xs text-gray-500">{workspace.memberIds.length} members</div>
        )}
      </div>

      {isSelected && (
        <Check className="w-4 h-4 text-blue-600 shrink-0" />
      )}
    </button>
  );
};

export default WorkspaceSelector;